import React, { useState } from 'react';
import { Box, Button, Link, Text } from '@opendoor/bricks/core';

interface School {
  gradesOffered?: Array<string> | null;
  name?: string;
  rating?: string;
}

interface SchoolDetailsProps {
  schools?: Array<School> | null;
}

const SchoolDetails: React.FC<SchoolDetailsProps> = ({ schools }) => {
  // Sort schools by school rating (in order of A+, A, A-, B+, B, ....)
  // Also accounts for when rating is not available
  const sortedSchools = schools
    ?.map((x) => {
      return {
        ...x,
        rating: x.rating ? x.rating + ',' : '',
      };
    })
    .sort((a, b) => {
      if (!a.rating) {
        return 1;
      }
      if (!b.rating) {
        return -1;
      }
      if (a.rating[0] < b.rating[0]) {
        return -1;
      }
      if (b.rating[0] > a.rating[0]) {
        return 1;
      }
      if (a.rating[0] === b.rating[0]) {
        if (a.rating[1] && a.rating[1] === '+') {
          return -1;
        }
        return 0;
      }
      return 0;
    })
    .map((x) => {
      return {
        ...x,
        rating: x.rating.slice(0, -1),
      };
    });

  const [showSchools, setShowSchools] = useState(false);
  return (
    <Box>
      <Text as="h2" fontSize="s3" lineHeight="s3" fontWeight="bold">
        Schools
      </Text>
      {!sortedSchools?.length ? (
        <Text fontSize="s1" lineHeight="s1" color="neutrals90" mt="1.5rem">
          No schools
        </Text>
      ) : (
        <>
          <Box
            mt="1.5rem"
            display="grid"
            gridRowGap="1rem"
            gridTemplateColumns="minmax(240px, 1fr) 1fr"
          >
            {sortedSchools.slice(0, showSchools ? undefined : 3).map((school) => (
              <React.Fragment key={school.name}>
                <Text fontSize="s1" lineHeight="s1" color="neutrals90">
                  {school.name}, {school.gradesOffered?.[0]}
                </Text>
                <Text
                  pl="7"
                  fontSize="s1"
                  lineHeight="s1"
                  fontWeight="semibold"
                  justifySelf="start"
                >
                  {school.rating}
                </Text>
              </React.Fragment>
            ))}
          </Box>
          {!!sortedSchools.slice(3).length && (
            <Button
              p="0"
              mt={[4, 5]}
              width="auto"
              height="auto"
              fontSize="s0"
              lineHeight="s0"
              color="brand50"
              alignItems="center"
              textDecoration="none"
              variant="link"
              aria-label="All Schools"
              backgroundColor="transparent"
              onClick={() => setShowSchools(!showSchools)}
              analyticsName="cosmos-pdp-school-data"
            >
              {showSchools ? 'Show less' : 'Show more'}
            </Button>
          )}
          <Text mt="1rem" color="neutrals90" fontSize="s00" lineHeight="s00">
            School data provided by&nbsp;
            <Link
              target="_blank"
              color="brand50"
              href="https://www.niche.com"
              aria-label="Go to learn more about Niche a provider of school data"
              analyticsName="cosmos-pdp-school-data-niche"
            >
              Niche
            </Link>
          </Text>
        </>
      )}
    </Box>
  );
};

export default SchoolDetails;
